import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper to format API errors
export function formatApiError(error) {
  const detail = error.response?.data?.detail;
  if (detail == null) return 'Si è verificato un errore. Riprova.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

// Auth
export const authApi = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  refresh: () => api.post('/auth/refresh'),
  exchangeSession: (sessionId) => api.post('/auth/session', { session_id: sessionId }),
};

// Products
export const productsApi = {
  getAll: (params) => api.get('/products', { params }),
  getOne: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
};

// Cart
export const cartApi = {
  get: () => api.get('/cart'),
  add: (item) => api.post('/cart/add', item),
  update: (item) => api.put('/cart/update', item),
  remove: (productId, size, color) => api.delete(`/cart/remove/${productId}?size=${size}&color=${color}`),
  clear: () => api.delete('/cart/clear'),
};

// Wishlist
export const wishlistApi = {
  get: () => api.get('/wishlist'),
  add: (productId) => api.post(`/wishlist/add/${productId}`),
  remove: (productId) => api.delete(`/wishlist/remove/${productId}`),
};

// Orders
export const ordersApi = {
  getAll: () => api.get('/orders'),
  getOne: (id) => api.get(`/orders/${id}`),
};

// Checkout
export const checkoutApi = {
  create: (data) => api.post('/checkout/create', data),
  getStatus: (sessionId) => api.get(`/checkout/status/${sessionId}`),
  capturePaypal: (data) => api.post('/paypal/capture', data),
  paypalCreateOrder: (orderId) => api.post('/paypal/create-order', { order_id: orderId }),
};

// Trade-in
export const tradeInApi = {
  getEligible: () => api.get('/trade-in/eligible'),
  request: (data) => api.post('/trade-in/request', data),
  getHistory: () => api.get('/trade-in/history'),
};

export const prelovedApi = {
  getAll: () => api.get('/preloved'),
};

// Reviews
export const reviewsApi = {
  create: (data) => api.post('/reviews', data),
  getForProduct: (productId) => api.get(`/products/${productId}/reviews`),
};

// GDPR account rights
export const accountApi = {
  exportData: () => api.get('/account/export'),
  deleteAccount: () => api.delete('/account'),
};

// Wardrobe
export const wardrobeApi = {
  get: () => api.get('/wardrobe'),
  getOutfits: () => api.get('/wardrobe/outfits'),
  saveOutfit: (name, productIds) => api.post('/wardrobe/outfits', { name, product_ids: productIds }),
  deleteOutfit: (outfitId) => api.delete(`/wardrobe/outfits/${outfitId}`),
  shareOutfit: (outfitId) => api.post(`/wardrobe/outfits/${outfitId}/share`),
  getSharedOutfit: (token) => api.get(`/shared/outfits/${token}`),
  suggest: (productIds) => api.post('/wardrobe/suggest', { product_ids: productIds }),
};

export const notificationsApi = {
  getAll: () => api.get('/notifications'),
  markAllRead: () => api.post('/notifications/read-all'),
};

// Admin
export const adminApi = {
  getStats: () => api.get('/admin/stats'),
  getOrders: (status) => api.get('/admin/orders', { params: { status } }),
  updateOrderStatus: (orderId, status, extra = {}) => api.put(`/admin/orders/${orderId}/status`, { status, ...extra }),
  getTradeIns: (status) => api.get('/admin/trade-ins', { params: { status } }),
  updateTradeInStatus: (tradeInId, status, reason) => api.put(`/admin/trade-ins/${tradeInId}/status`, { status, reason }),
  getReviews: (status) => api.get('/admin/reviews', { params: { status } }),
  updateReviewStatus: (reviewId, status) => api.put(`/admin/reviews/${reviewId}/status`, { status }),
};

export default api;
